/**
 * Single render document for the editor artboard, fullscreen preview, HTML download and PNG export.
 * Every output uses the same markup wrapper (.mc-html-host > .mc-html-stage-inner) and the same CSS.
 */
import { applyElementLayout } from './mediaCreatorElementLayout';
import { normalizeArtboardDom, queryLogoImages } from './mediaCreatorDomRegistry';
import {
  applyCutoutState,
  CUTOUT_ATTR,
  lookupAssetUrl,
  renditionForAsset,
  resolveLogoUrl,
  resolvePhotoUrl,
} from './mediaCreatorHtmlResolve';
import {
  applyPhotoZoom,
  clampPhotoZoom,
  CUTOUT_OBJECT_FIT,
  objectPositionForPhoto,
  PHOTO_OBJECT_FIT,
} from './mediaCreatorPhotoFit';
import { sanitizeCssForExport } from './mediaCreatorExportCss';

export const MC_FONT_STYLESHEET_URL =
  'https://fonts.googleapis.com/css2?family=Montserrat:wght@600;700;800&family=Source+Sans+3:wght@400;600;700&display=swap';

export const MC_FALLBACK_LOGO_PATH = '/selkirk-logo.svg';

/** Selector scope shared by the editor DOM and the render document. */
export const MC_SCOPE = '.mc-html-host .mc-html-stage-inner';

/** Legacy host CSS (scoped to .mc-html-stage); kept for the html2canvas fallback path. */
export const MC_HTML_LAYOUT_CSS = `
  .mc-html-stage {
    width: var(--mc-width, 1920px);
    height: var(--mc-height, 1080px);
    margin: 0 auto;
    overflow: hidden;
    box-sizing: border-box;
  }
  .mc-html-stage .mc-invite {
    width: 100%;
    height: 100%;
    min-height: 100%;
    box-sizing: border-box;
    overflow: hidden;
    align-items: stretch;
  }
  .mc-html-stage .mc-brand,
  .mc-html-stage img[data-logo-slot] {
    background: transparent !important;
    object-fit: contain;
    max-height: 140px;
    width: auto;
    display: block;
  }
  .mc-html-stage .mc-logo-wrap img[data-logo-slot],
  .mc-html-stage .mc-logo-wrap .mc-brand {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    object-position: left center;
  }
  .mc-html-stage .mc-gallery {
    min-height: 100%;
    align-self: stretch;
    box-sizing: border-box;
  }
  .mc-html-stage .mc-gallery .mc-photo {
    overflow: hidden;
    min-height: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    position: static;
    width: auto;
    padding-bottom: 0 !important;
  }
  .mc-html-stage .mc-gallery .mc-photo img {
    position: static;
  }
  .mc-html-stage .mc-gallery img[data-photo-slot] {
    width: 100%;
    height: 100%;
    min-height: 320px;
    flex: 1 1 auto;
    object-fit: cover;
    display: block;
  }
  .mc-html-stage [data-editable] { outline: none; }
  .mc-html-stage .mc-slot-caption {
    pointer-events: none;
    user-select: none;
    position: absolute;
    left: 1rem;
    bottom: 1rem;
    z-index: 1;
    font-family: Montserrat, Arial, sans-serif;
    font-weight: 900;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #fff;
    text-shadow: 0 2px 10px rgba(0,0,0,0.55);
  }
`;

/**
 * Base image sizing (previously app-wide in DocumentDesign.css). Must precede host layout CSS:
 * equal-specificity host rules win, matching the editor cascade.
 */
const BASE_IMAGE_CSS = `
  ${MC_SCOPE} img[data-photo-slot],
  ${MC_SCOPE} img[data-logo-slot] {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

/** Layout rules that previously lived in the editor-only host style; follow document_css. */
const POST_DOCUMENT_CSS = `
  ${MC_SCOPE} .mc-copy {
    position: relative;
    z-index: 1;
  }
  ${MC_SCOPE} .mc-slot-caption {
    display: none;
  }
  ${MC_SCOPE} .mc-photo-core::before,
  ${MC_SCOPE} .mc-photo-drill::before,
  ${MC_SCOPE} .mc-photo-site::before,
  ${MC_SCOPE} figure.mc-photo::before {
    content: none !important;
    display: none !important;
  }
  ${MC_SCOPE} [data-mc-cutout] {
    background: transparent !important;
    box-shadow: none !important;
  }
  ${MC_SCOPE} [data-mc-cutout]::before,
  ${MC_SCOPE} [data-mc-cutout]::after {
    content: none !important;
    display: none !important;
  }
  ${MC_SCOPE} img[data-mc-cutout] {
    object-fit: contain !important;
    min-height: 0;
  }
`;

function hostLayoutCss() {
  return MC_HTML_LAYOUT_CSS.replace(/\.mc-html-stage/g, MC_SCOPE);
}

function roundPx(n) {
  return Math.round(n * 1000) / 1000;
}

/**
 * Convert vw / vh / vmin / vmax to px against the artboard (not the browser window).
 * @returns {{ css: string, count: number }}
 */
export function freezeViewportUnits(css, w = 1920, h = 1080) {
  if (!css) return { css: css || '', count: 0 };
  let count = 0;
  const out = css.replace(
    /(^|[^\w.#-])(-?(?:\d+\.?\d*|\.\d+))(vw|vh|vmin|vmax)\b/gi,
    (match, pre, num, unit) => {
      const n = parseFloat(num);
      if (!Number.isFinite(n)) return match;
      const u = unit.toLowerCase();
      let basis = w;
      if (u === 'vh') basis = h;
      else if (u === 'vmin') basis = Math.min(w, h);
      else if (u === 'vmax') basis = Math.max(w, h);
      count += 1;
      return `${pre}${roundPx((n * basis) / 100)}px`;
    },
  );
  return { css: out, count };
}

function findBlockEnd(css, openIdx) {
  let depth = 0;
  for (let i = openIdx; i < css.length; i += 1) {
    const ch = css[i];
    if (ch === '{') depth += 1;
    else if (ch === '}') {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}

/** @returns {boolean | null} null when the query cannot be evaluated statically. */
export function evaluateMediaQuery(query, w, h) {
  const parts = String(query || '').split(',').map((s) => s.trim()).filter(Boolean);
  if (!parts.length) return true;
  let anyUnknown = false;
  for (const part of parts) {
    const res = evaluateMediaQueryPart(part, w, h);
    if (res === true) return true;
    if (res === null) anyUnknown = true;
  }
  return anyUnknown ? null : false;
}

function evaluateMediaQueryPart(part, w, h) {
  let text = part.toLowerCase().trim();
  let negate = false;
  if (text.startsWith('not ')) {
    negate = true;
    text = text.slice(4).trim();
  }
  text = text.replace(/^only\s+/, '');
  const tokens = text.split(/\s+and\s+/).map((t) => t.trim()).filter(Boolean);
  let result = true;
  for (const tok of tokens) {
    if (tok === 'screen' || tok === 'all') continue;
    if (tok === 'print' || tok === 'speech') {
      result = false;
      continue;
    }
    const m = tok.match(/^\(\s*(min|max)-(width|height)\s*:\s*(\d+(?:\.\d+)?)px\s*\)$/);
    if (!m) return null;
    const value = parseFloat(m[3]);
    const actual = m[2] === 'width' ? w : h;
    const ok = m[1] === 'min' ? actual >= value : actual <= value;
    if (!ok) result = false;
  }
  return negate ? !result : result;
}

/**
 * Evaluate @media width/height queries against the artboard: drop non-matching blocks, unwrap matching.
 * @returns {{ css: string, count: number }}
 */
export function resolveMediaQueries(css, w = 1920, h = 1080) {
  if (!css) return { css: css || '', count: 0 };
  let out = '';
  let i = 0;
  let count = 0;
  const re = /@media\b([^{]*)\{/gi;
  let m;
  while ((m = re.exec(css)) !== null) {
    const openIdx = m.index + m[0].length - 1;
    const endIdx = findBlockEnd(css, openIdx);
    if (endIdx < 0) break;
    const verdict = evaluateMediaQuery(m[1], w, h);
    out += css.slice(i, m.index);
    if (verdict === null) {
      out += css.slice(m.index, endIdx + 1);
    } else {
      count += 1;
      if (verdict) {
        out += resolveMediaQueries(css.slice(openIdx + 1, endIdx), w, h).css;
      }
    }
    i = endIdx + 1;
    re.lastIndex = i;
  }
  out += css.slice(i);
  return { css: out, count };
}

/** document_css normalized for the artboard: sanitized, media queries resolved, viewport units frozen. */
export function compileDocumentCss(css, w = 1920, h = 1080) {
  const sanitized = sanitizeCssForExport(css || '');
  const media = resolveMediaQueries(sanitized, w, h);
  const frozen = freezeViewportUnits(media.css, w, h);
  return {
    css: frozen.css,
    stats: { viewportUnitsFrozen: frozen.count, mediaQueriesResolved: media.count },
  };
}

function artboardSize(layout) {
  return {
    w: layout?.size_px?.width || 1920,
    h: layout?.size_px?.height || 1080,
  };
}

/** @returns {{ css: string, stats: object }} */
export function buildArtboardCssWithStats(layout) {
  const { w, h } = artboardSize(layout);
  const doc = compileDocumentCss(layout?.document_css || '', w, h);
  const css = [
    `${MC_SCOPE} { width: ${w}px; height: ${h}px; }`,
    BASE_IMAGE_CSS,
    hostLayoutCss(),
    doc.css,
    POST_DOCUMENT_CSS,
  ].join('\n');
  return { css, stats: doc.stats };
}

/** Canonical CSS for the artboard in every output. */
export function buildArtboardCss(layout) {
  return buildArtboardCssWithStats(layout).css;
}

function absoluteUrl(path) {
  if (typeof window === 'undefined' || !path?.startsWith('/')) return path;
  return `${window.location.origin}${path}`;
}

/** Apply layout.photo_zoom to every slot img (after element_layout, which owns the base transform). */
export function applySlotZooms(root, layout) {
  if (!root) return;
  const bindings = layout?.photo_bindings || {};
  const zooms = layout?.photo_zoom || {};
  root.querySelectorAll('img[data-photo-slot]').forEach((img) => {
    const slot = img.getAttribute('data-photo-slot');
    const bound = slot && bindings[slot] != null;
    const zoom = bound
      ? clampPhotoZoom(zooms[slot] ?? 1, { cutout: img.hasAttribute(CUTOUT_ATTR) })
      : 1;
    applyPhotoZoom(img, zoom, img.style.objectPosition);
  });
}

/**
 * Hydrate snapshot HTML: set Brand Library src + object-position, apply element_layout once.
 * Mirrors MediaCreatorHtmlArtboard.hydrateImages.
 */
export function hydrateMarkup(documentHtml, layout, assetUrls) {
  const bindings = layout?.photo_bindings || {};
  const slotPositions = layout?.photo_object_positions || {};
  const logoId = layout?.logo_binding;

  const parsed = new DOMParser().parseFromString(documentHtml || '', 'text/html');
  const root = parsed.querySelector('.mc-invite') || parsed.body.firstElementChild;
  if (!root) return documentHtml || '';

  normalizeArtboardDom(root);

  queryLogoImages(root).forEach((img) => {
    const resolved = resolveLogoUrl(logoId, assetUrls);
    const url = resolved || (logoId && img.getAttribute('src') ? null : absoluteUrl(MC_FALLBACK_LOGO_PATH));
    if (url) img.setAttribute('src', url);
    if (logoId) {
      img.setAttribute('data-media-asset-id', String(logoId));
      img.setAttribute(
        'data-rendition',
        renditionForAsset(lookupAssetUrl(assetUrls, logoId), { logo: true }),
      );
    }
  });

  root.querySelectorAll('img[data-photo-slot]').forEach((img) => {
    const slot = img.getAttribute('data-photo-slot');
    const assetId = slot ? bindings[slot] : null;
    const url = resolvePhotoUrl(assetId, assetUrls);
    if (url && assetId) {
      const info = lookupAssetUrl(assetUrls, assetId);
      img.setAttribute('src', url);
      img.setAttribute('data-media-asset-id', String(assetId));
      img.setAttribute('data-rendition', renditionForAsset(info));
      const cutout = applyCutoutState(img, info);
      img.style.objectFit = cutout ? CUTOUT_OBJECT_FIT : PHOTO_OBJECT_FIT;
      img.style.objectPosition = cutout
        ? slotPositions[slot] || '50% 50%'
        : objectPositionForPhoto(assetUrls, assetId, [slotPositions[slot], '50% 50%']);
    } else {
      applyCutoutState(img, null);
      img.removeAttribute('src');
      img.removeAttribute('data-media-asset-id');
    }
  });

  applyElementLayout(root, layout?.element_layout || {});
  applySlotZooms(root, layout);

  return root.outerHTML;
}

/**
 * Hydrate markup, then replace every img src with a data URL (no network access at render time).
 * @param {object} layout
 * @param {object} opts
 * @param {object} opts.assetUrls
 * @param {(img: HTMLImageElement) => Promise<string | null>} opts.resolveDataUrl
 * @returns {Promise<{ innerHtml: string, inlined: number, failures: string[] }>}
 */
export async function hydrateMarkupInline(layout, { assetUrls, resolveDataUrl }) {
  const hydrated = hydrateMarkup(layout?.document_html || '', layout, assetUrls);
  const doc = new DOMParser().parseFromString(hydrated, 'text/html');
  const root = doc.querySelector('.mc-invite') || doc.body.firstElementChild;
  if (!root) return { innerHtml: hydrated, inlined: 0, failures: [] };
  const imgs = [...root.querySelectorAll('img[src]')];
  const failures = [];
  let inlined = 0;
  await Promise.all(
    imgs.map(async (img) => {
      const src = img.getAttribute('src') || '';
      if (src.startsWith('data:')) return;
      let dataUrl = null;
      try {
        dataUrl = await resolveDataUrl(img);
      } catch {
        dataUrl = null;
      }
      if (dataUrl) {
        img.setAttribute('src', dataUrl);
        inlined += 1;
      } else {
        failures.push(img.getAttribute('data-media-asset-id') || src.slice(0, 80));
      }
    }),
  );
  return { innerHtml: root.outerHTML, inlined, failures };
}

function escapeHtmlText(s) {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;');
}

/** Host wrapper markup identical to the editor artboard (unscaled). */
export function buildRenderBody(innerHtml, layout) {
  const { w, h } = artboardSize(layout);
  return (
    `<div class="mc-html-host" style="width:${w}px;height:${h}px;position:relative;overflow:hidden;">`
    + `<div class="mc-html-stage-inner" style="width:${w}px;height:${h}px;">${innerHtml}</div>`
    + '</div>'
  );
}

/**
 * Full HTML document for one composition.
 * @param {object} layout viewer snapshot
 * @param {object} opts
 * @param {object} [opts.assetUrls]
 * @param {string} [opts.innerHtml] pre-hydrated markup (overrides hydration)
 * @param {string} [opts.fontCss] embedded @font-face CSS (inline mode); otherwise a Google Fonts link
 * @param {string} [opts.title]
 * @param {string} [opts.headExtra]
 * @param {string} [opts.bodyBackground]
 */
export function buildRenderDocument(layout, opts = {}) {
  const { w, h } = artboardSize(layout);
  const inner = opts.innerHtml ?? hydrateMarkup(layout?.document_html || '', layout, opts.assetUrls);
  const css = buildArtboardCss(layout);
  const fontHead = opts.fontCss != null
    ? `<style data-mc-fonts="embedded">${opts.fontCss}</style>`
    : (
      '<link rel="preconnect" href="https://fonts.googleapis.com" />'
      + '<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />'
      + `<link href="${MC_FONT_STYLESHEET_URL}" rel="stylesheet" />`
    );
  const bg = opts.bodyBackground || '#111';
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=${w}" />
<title>${escapeHtmlText(opts.title || layout?.title || 'Media Creator')}</title>
${fontHead}
<style>
html, body { margin: 0; padding: 0; background: ${bg}; }
body { min-width: ${w}px; min-height: ${h}px; }
.mc-html-host { margin: 0 auto; }
${css}
</style>
${opts.headExtra || ''}
</head>
<body>
${buildRenderBody(inner, layout)}
</body>
</html>`;
}
